import React, { useRef, useMemo, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
// Note: fetchLatestWins API endpoint not available
// import { fetchLatestWins } from '@/services/api';
import styles from './LatestWins.module.css';

/**
 * Latest Wins Component
 * Displays recent big wins in a marquee-style scrolling display
 */

// Crypto config for currency icons and formatting (using coin icons from /assets/images/coins/)
const cryptoConfig = {
  USDT: {
    icon: '/assets/images/coins/USDT.webp',
    symbol: '$',
    minBet: 50000,
    maxBet: 500000,
    decimals: 0
  },
  INR: {
    icon: '/assets/images/coins/INR.webp',
    symbol: 'INR',
    minBet: 5000,
    maxBet: 50000,
    decimals: 0
  },
  PKR: {
    icon: '/assets/images/coins/INR.webp', // Fallback to INR if PKR not available
    symbol: '$',
    minBet: 5000,
    maxBet: 50000,
    decimals: 0
  },
  BTC: {
    icon: '/assets/images/coins/BTC.webp',
    symbol: 'BTC',
    minBet: 0.001,
    maxBet: 0.1,
    decimals: 8
  },
};

// Generate unique usernames (like old code)
const generateUsernames = (count) => {
  const nameParts = [
    "Crypto", "Bitcoin", "Satoshi", "Hodl", "Moon", "Rocket", "Diamond", "Whale", "Bull", "Bear", "Degen", "Ape", "Chain", "Block", "Hash",
    "Simon", "Alex", "Max", "Elon", "Vital", "Sam", "Mike", "John", "Lisa", "Emma", "Sophia", "Jack", "Ryan", "Gia", "Leo", "Zack",
    "Player", "Gamer", "Winner", "Lucky", "Pro", "Elite", "Master", "Champ", "Star", "King", "Queen", "Ace", "Hero", "Legend", "Boss"
  ];

  const nameSuffixes = [
    "TheMoon", "Hodler", "Trader", "Winner", "Pro", "Master", "King", "Queen", "Guru", "Wizard", "Ninja", "Whale", "Shark", "Hunter", "Boss",
    "123", "007", "2024", "99", "88", "777", "666", "555", "444", "333", "22", "11", "00", "X", "Z", "Y", "Alpha", "Beta", "Omega", "Prime"
  ];

  const usedUsernames = new Set();
  const usernames = [];

  for (let i = 0; i < count; i++) {
    let username = "";
    let attempts = 0;

    do {
      const pattern = Math.floor(Math.random() * 4);
      switch (pattern) {
        case 0:
          username = `${nameParts[Math.floor(Math.random() * nameParts.length)].substring(0, 4)}${Math.floor(Math.random() * 100).toString().padStart(2, '0')}`;
          break;
        case 1:
          username = `${nameParts[Math.floor(Math.random() * nameParts.length)].substring(0, 5)}${Math.floor(Math.random() * 10)}`;
          break;
        case 2:
          const name1 = nameParts[Math.floor(Math.random() * nameParts.length)].substring(0, 3);
          const name2 = nameParts[Math.floor(Math.random() * nameParts.length)].substring(0, 3);
          username = `${name1}${name2}`;
          break;
        case 3:
          username = nameParts[Math.floor(Math.random() * nameParts.length)].substring(0, 6);
          break;
      }
      attempts++;
      if (attempts > 100) {
        username = `User${Math.floor(Math.random() * 100)}`;
        break;
      }
    } while (usedUsernames.has(username) || username.length > 6);

    usedUsernames.add(username);
    usernames.push(username);
  }

  return usernames;
};

// Generate random profit (like old code)
const generateRandomProfit = (min, max, decimals) => {
  const factor = 10 ** decimals;
  return ((Math.random() * (max - min) + min) * factor).toFixed(decimals) / factor;
};

// Format profit amount (matching old code exactly)
const formatProfit = (amount, currency) => {
  const config = cryptoConfig[currency] || cryptoConfig.USDT;
  const { symbol } = config;
  const numAmount = Number(amount) || 0;

  if (currency === 'BTC') {
    // For BTC, show 4 decimal places max to keep it short
    return `${symbol}${Number(numAmount).toFixed(4)}`;
  }

  if (numAmount >= 100000) {
    // Convert to M for millions
    return `${symbol}${(numAmount / 1000000).toFixed(1)}M`;
  }

  if (numAmount >= 1000) {
    // Convert to K for thousands
    return `${symbol}${(numAmount / 1000).toFixed(1)}K`;
  }

  // For small amounts, just show the number
  return `${symbol}${Math.round(numAmount)}`;
};

const LatestWins = ({ all = [], slots = [], liveCasino = [], providers = null, isLoading: propsLoading = false }) => {
  const marqueeRef = useRef(null);
  const [failedImages, setFailedImages] = useState(new Set());

  // Combine all props data (like old code: all={[...jsPragmeticSlots]} slots={jsPragmeticSlots} liveCasino={jsPragmeticSlots})
  // This uses the API fetched data from jsPragmeticSlots
  const combinedPropsData = useMemo(() => {
    // Merge all arrays and remove duplicates
    const merged = [...all, ...slots, ...liveCasino];
    let unique = merged.filter((item, index, self) => {
      const itemId = item.id || item.uuid || item.game_uid;
      const firstIndex = self.findIndex((t) => (t.id || t.uuid || t.game_uid) === itemId);
      return index === firstIndex;
    });
    
    // Filter by providers if provided
    if (providers && Array.isArray(providers) && providers.length > 0) {
      // Map user-provided provider names to exact API provider names
      const providerNameMap = {
        'Betsoft': ['Betsoft', 'betsoft', 'Bet Soft', 'bet soft'],
        'Igrosoft': ['Igrosoft', 'igrosoft', 'Igro Soft', 'igro soft'],
        'OneTouch': ['OneTouch', 'onetouch', 'One Touch', 'one touch', '1Touch', '1touch'],
        'BFGames': ['BFGames', 'bfgames', 'BF Games', 'bf games'],
        'BoomingGames': ['BoomingGames', 'boominggames', 'Booming Games', 'booming games'],
        // 'PragmaticPlay': ['PragmaticPlay', 'pragmaticplay', 'Pragmatic Play', 'pragmatic play', 'Pragmatic', 'pragmatic'],
      };
      
      // Create a set of all possible provider name variations to match
      const providerVariations = new Set();
      providers.forEach(provider => {
        const normalized = provider.trim();
        // Add the exact name
        providerVariations.add(normalized);
        // Add normalized versions (lowercase, no spaces)
        providerVariations.add(normalized.toLowerCase().replace(/\s+/g, ''));
        // Add mapped variations if they exist
        if (providerNameMap[normalized]) {
          providerNameMap[normalized].forEach(variant => {
            providerVariations.add(variant);
            providerVariations.add(variant.toLowerCase().replace(/\s+/g, ''));
          });
        }
      });
      
      const beforeFilterCount = unique.length;
      unique = unique.filter((item) => {
        const gameProvider = (item.provider || item.vendor || '').trim();
        const gameProviderNormalized = gameProvider.toLowerCase().replace(/\s+/g, '');
        
        // Check if game provider matches any variation
        const matches = providerVariations.has(gameProvider) || 
                       providerVariations.has(gameProviderNormalized) ||
                       Array.from(providerVariations).some(variant => {
                         const variantNormalized = typeof variant === 'string' ? variant.toLowerCase().replace(/\s+/g, '') : '';
                         return gameProviderNormalized === variantNormalized || 
                                gameProviderNormalized.includes(variantNormalized) ||
                                variantNormalized.includes(gameProviderNormalized);
                       });
        return matches;
      });
      
      // Debug: Log if all games were filtered out
      if (beforeFilterCount > 0 && unique.length === 0) {
        console.warn('LatestWins: All games filtered out by providers. Available providers in games:', 
          [...new Set(merged.map(g => g.provider || g.vendor || 'unknown'))]
        );
      }
    }
    
    return unique;
  }, [all, slots, liveCasino, providers]);

  // Use React Query for data fetching (fallback if no props data)
  // Note: API endpoint not available, so disabled
  const { data, isLoading } = useQuery({
    queryKey: ['latestWins'],
    queryFn: async () => {
      // API endpoint /api/latest-wins does not exist
      return [];
    },
    enabled: false, // Disabled - API endpoint not available
    staleTime: 30000,
    refetchInterval: 30000,
    retry: 1,
  });

  // Process and map the wins data properly (like old code pattern)
  // Priority: Use props data if available, otherwise use API data
  const processedWins = useMemo(() => {
    // If we have props data, use it (like old code: all={[...jsPragmeticSlots]} slots={jsPragmeticSlots} liveCasino={jsPragmeticSlots})
    if (combinedPropsData.length > 0) {
      // Generate unique usernames for all games (like old code)
      const uniqueUsernames = generateUsernames(combinedPropsData.length);
      
      return combinedPropsData.map((game, index) => {
        // Map game data to win structure (similar to old code pattern)
        // Data structure from API: { id, uuid, game_uid, name, game_name, game_icon, image, game_type, provider, source }
        const currencies = ['USDT', 'INR', 'BTC'];
        const currency = game.currency || game.currency_code || currencies[Math.floor(Math.random() * currencies.length)];
        const currencyConfig = cryptoConfig[currency] || cryptoConfig.USDT;
        const { minBet, maxBet, decimals } = currencyConfig;
        
        // Generate random profit like old code
        const profitAmount = game.profit || game.amount || game.win_amount || generateRandomProfit(minBet, maxBet, decimals);
        const formattedProfit = formatProfit(profitAmount, currency);

        // Validate and clean image URL
        const gameIcon = game.game_icon || game.image || '';
        const validImageUrl = gameIcon && 
                             gameIcon !== '/assets/images/placeholder.png' &&
                             gameIcon !== 'undefined' &&
                             gameIcon !== 'null' &&
                             gameIcon.trim() !== '' &&
                             (gameIcon.startsWith('http') || gameIcon.startsWith('/'));

        return {
          id: game.id || game.uuid || game.game_uid || `game-${index + 1}`,
          username: game.username || game.player_name || game.player || uniqueUsernames[index],
          game_name: game.name || game.game_name || 'Game',
          game_icon: validImageUrl ? gameIcon : null, // Set to null if invalid, will be filtered out
          profit: formattedProfit,
          currency: currency,
          currencyIcon: currencyConfig.icon,
        };
      });
    }

    // Fallback to API data (if available in future)
    // Handle undefined data from disabled query
    const apiData = data || [];
    if (!Array.isArray(apiData) || apiData.length === 0) {
      return [];
    }

    return apiData.map((win, index) => {
      // Map API response fields to component structure
      const currency = win.currency || win.currency_code || 'USDT';
      const currencyConfig = cryptoConfig[currency] || cryptoConfig.USDT;
      const profitAmount = win.profit || win.amount || win.win_amount || 0;
      const formattedProfit = formatProfit(profitAmount, currency);

      // Validate and clean image URL
      const winIcon = win.game_icon || win.image || win.game_image || '';
      const validWinImageUrl = winIcon && 
                              winIcon !== '/assets/images/placeholder.png' &&
                              winIcon !== 'undefined' &&
                              winIcon !== 'null' &&
                              winIcon.trim() !== '' &&
                              (winIcon.startsWith('http') || winIcon.startsWith('/'));

      return {
        id: win.id || win.bet_id || index + 1,
        username: win.username || win.player_name || win.player || `Player${index + 1}`,
        game_name: win.game_name || win.game || 'Game',
        game_icon: validWinImageUrl ? winIcon : null, // Set to null if invalid, will be filtered out
        profit: formattedProfit,
        currency: currency,
        currencyIcon: currencyConfig.icon,
      };
    });
  }, [combinedPropsData, data]);
  
  // Debug logging (can be removed in production)
  if (combinedPropsData.length === 0 && providers) {
    console.log('LatestWins: No games after provider filter', {
      providers,
      allCount: all.length,
      slotsCount: slots.length,
      liveCasinoCount: liveCasino.length,
    });
  }

  // Filter out wins with failed images or invalid image URLs
  const validWins = useMemo(() => {
    return processedWins.filter((win) => {
      // Skip items where image has failed to load
      if (failedImages.has(win.id)) {
        return false;
      }
      // Skip items with invalid or missing image URLs
      if (!win.game_icon || 
          win.game_icon === null ||
          win.game_icon === '/assets/images/placeholder.png' ||
          win.game_icon.trim() === '' ||
          win.game_icon === 'undefined' ||
          win.game_icon === 'null') {
        return false;
      }
      return true;
    });
  }, [processedWins, failedImages]);

  // Duplicate wins for seamless continuous loop
  // Duplicate twice so when animation reaches -50%, it loops back seamlessly
  const seamlessWins = useMemo(() => {
    if (validWins.length === 0) return [];
    return [...validWins, ...validWins];
  }, [validWins]);

  // Handle image load errors
  const handleImageError = (winId, event) => {
    // Prevent infinite loop by checking if we've already marked this image as failed
    if (!failedImages.has(winId)) {
      setFailedImages((prev) => new Set([...prev, winId]));
      // Set placeholder to prevent broken image icon
      if (event.target) {
        event.target.src = '/assets/images/placeholder.png';
        // Hide the image if placeholder also fails
        event.target.onerror = () => {
          event.target.style.display = 'none';
        };
      }
    }
  };

  // Show loading if props are loading or if fetching from API and no props data
  const showLoading = (propsLoading && combinedPropsData.length === 0) || (isLoading && combinedPropsData.length === 0 && processedWins.length === 0);

  if (showLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.greenDot}></div>
          <h2 className={styles.title}>Recent Big Wins</h2>
        </div>
        <div className={styles.loadingContainer}>
          <p>Loading wins...</p>
        </div>
      </div>
    );
  }

  if (validWins.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.greenDot}></div>
          <h2 className={styles.title}>Recent Big Wins</h2>
        </div>
        <div className={styles.emptyState}>
          <p>No wins to display</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.greenDot}></div>
        <h2 className={styles.title}>Recent Big Wins</h2>
      </div>
      
      <div className={styles.sliderContainer}>
        <div className={styles.marqueeContainer}>
          <div
            ref={marqueeRef}
            className={styles.marqueeContent}
          >
            {seamlessWins.map((win, index) => (
              <div key={`${win.id}-${index}`} className={styles.winCard}>
                <div className={styles.gameImageContainer}>
                  <img
                    src={win.game_icon}
                    alt={win.game_name}
                    className={styles.gameImage}
                    loading="lazy"
                    onError={(e) => handleImageError(win.id, e)}
                  />
                </div>
                <div className={styles.userInfo}>
                  <div className={styles.userBadgeContainer}>
                    <img
                      src="/assets/images/plate.webp"
                      alt="VIP badge"
                      className={styles.userBadge}
                    />
                    <span className={styles.username}>{win.username}</span>
                  </div>
                  <div className={styles.profitContainer}>
                    <img
                      src={win.currencyIcon}
                      alt={win.currency}
                      className={styles.currencyIcon}
                    />
                    <span className={styles.profitAmount}>{win.profit}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LatestWins;
